'use client'

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'

export type DialogContentSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full'

const dialogContentMaxWidth: Record<DialogContentSize, string> = {
  sm: '400px',
  md: '600px',
  lg: '900px',
  xl: '1100px',
  '2xl': '1200px',
  '3xl': '1300px',
  '4xl': '1400px',
  full: 'calc(100vw - 2rem)',
}

const getDialogContentMaxWidth = (size?: DialogContentSize) => {
  if (!size) return undefined

  return dialogContentMaxWidth[size]
}

const getDialogContentChildren = (children: React.ReactNode): React.ReactNode[] => {
  return React.Children.toArray(children).flatMap((child) => {
    if (
      React.isValidElement<{ children?: React.ReactNode }>(child) &&
      child.type === React.Fragment
    ) {
      return getDialogContentChildren(child.props.children)
    }

    return child
  })
}

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot='dialog-overlay'
      forceRender
      className={cn(
        'fixed inset-0 isolate z-2050 transition-backdrop-blur bg-black/10 duration-100 not-hover:supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  bodyClassName,
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  size,
  style,
  scrollable = false,
  ...props
}: DialogPrimitive.Popup.Props & {
  bodyClassName?: string
  overlayClassName?: string
  showCloseButton?: boolean
  size?: DialogContentSize
  scrollable?: boolean
}) {
  const maxWidth = getDialogContentMaxWidth(size)
  const childItems = getDialogContentChildren(children)
  const headerChildren = childItems.filter(
    (child) => React.isValidElement(child) && child.type === DialogHeader
  )
  const footerChildren = childItems.filter(
    (child) => React.isValidElement(child) && child.type === DialogFooter
  )
  const bodyChildren = childItems.filter(
    (child) =>
      !React.isValidElement(child) || (child.type !== DialogHeader && child.type !== DialogFooter)
  )

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />

      <DialogPrimitive.Popup
        data-slot='dialog-content'
        className={cn(
          'fixed top-1/2 left-1/2 z-2050 flex max-h-[90vh] min-w-0 w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-hidden rounded-4xl bg-background p-6 text-sm ring-1 ring-foreground/10 duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          !size && 'max-w-md',
          className
        )}
        style={{
          ...(size ? { width: 'calc(100% - 2rem)', maxWidth } : undefined),
          ...style,
        }}
        initialFocus={false}
        {...props}
      >
        {headerChildren}
        {bodyChildren.length > 0 && (
          <div
            data-slot='dialog-body'
            className={cn(
              'min-h-0 min-w-0 w-full flex flex-auto flex-col gap-4 py-4 overflow-x-hidden overflow-y-auto',
              scrollable && 'overflow-y-auto',
              bodyClassName
            )}
          >
            {bodyChildren}
          </div>
        )}
        {footerChildren}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            render={<Button variant='ghost' className='absolute top-5 right-4' size='icon-sm' />}
          >
            <XIcon />
            <span className='sr-only'>Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-header'
      className={cn('flex min-w-0 shrink-0 flex-col gap-2 bg-background pr-8', className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'flex min-w-0 shrink-0 flex-col-reverse gap-2 bg-background sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn('leading-none font-medium text-lg', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
